import appManager from '@app/appManager';
import TasksModule from '@modules/TasksModule';
import InboxModule from '@modules/InboxModule';
import { releaseTask } from '@modules/SpeedToLeadTasksModule/mutations/releaseTask';
import { cancelAcceptanceExpiry } from '../acceptanceExpiry';

const logger = appManager.logger('SpeedToLead');

/**
 * Releases a claimed but un-dialled STL task when the CSR can no longer dial.
 *
 * Called from multiple independent event subscriptions (inbound call offered,
 * phone call placed terminating, CSR goes offline) — all have identical behavior.
 * The function is idempotent: if no pre-dial task is claimed, or after the first
 * invocation clears the state, subsequent calls are no-ops.
 *
 * No toast is shown. Local cleanup proceeds regardless of whether the
 * releaseTask mutation succeeds.
 *
 * @param reason - Human-readable trigger description logged for debugging (e.g. 'inbound-offered').
 */
async function handleStlPreDialRelease(reason: string): Promise<void> {
  const preDialTask = cancelAcceptanceExpiry();
  if (!preDialTask) return;

  const { taskId, tenantId } = preDialTask;

  logger.info('stl:pre-dial-release:releasing', { taskId, tenantId, reason });

  try {
    const released = await releaseTask({ taskId, tenantId });
    if (released) {
      logger.info('stl:pre-dial-release:released', { taskId });
    } else {
      logger.warn('stl:pre-dial-release:release-failed-returned-false', {
        taskId,
      });
    }
  } catch (err) {
    // Log silently — proceed with local cleanup regardless
    logger.error('stl:pre-dial-release:release-failed', { taskId, err });
  }

  TasksModule.deleteTask(taskId);
  InboxModule.setSelectedTaskId(undefined);
  logger.info('stl:pre-dial-release:local-cleanup-done', { taskId });
}

export default handleStlPreDialRelease;
